import { useEffect, useRef, useState } from "react";
import { ChevronUp, Play, Volume2, VolumeX } from "lucide-react";

import { NIVA_AVATAR_POSTER } from "@/components/NivaAvatar";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const INTRO_VIDEO = "/niva/niva-intro.mp4";
const INTRO_POSTER = "/niva/niva-intro.jpg";

/** Once she has introduced herself, she stays folded away for the rest of the
 *  tab — the drawer gets reopened constantly while browsing, and a 20-second
 *  greeting on every one of those would be an ad, not a welcome. Per-tab
 *  (sessionStorage) to match how the thread itself is stored. */
const SEEN_KEY = "bl.assistant.intro.seen";

function introSeen(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false; // private mode — she just greets once per drawer open
  }
}

function markIntroSeen(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* nothing to do — worst case she introduces herself again */
  }
}

/**
 * Niva's recorded introduction, pinned to the top of the assistant drawer.
 *
 * Starts muted, because every browser blocks autoplay with sound and a
 * rejected play() would leave a dead frame at the top of the panel. The clip
 * carries burned-in captions, so muted still reads — the speaker button is
 * there for anyone who wants her voice.
 *
 * The video element is unmounted, not hidden, while collapsed: a hidden
 * <video> keeps its buffer and its decoder, and this is a 1.1MB file sitting
 * above a chat the user actually came for.
 */
export function NivaIntro() {
  const reducedMotion = usePrefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [collapsed, setCollapsed] = useState(introSeen);
  const [muted, setMuted] = useState(true);
  // Optimistic: assume the muted autoplay below is allowed, so the tap-to-play
  // overlay doesn't flash over the first frame on every open. The play()
  // rejection and the pause event correct it if the browser says no.
  const [playing, setPlaying] = useState(!reducedMotion);
  // A missing/undecodable file should take the whole strip away rather than
  // leave a black box wedged above the transcript.
  const [failed, setFailed] = useState(false);

  // React sets `muted` as a property, but only on the initial render for some
  // browsers' fast paths — mirroring it onto the node keeps the speaker button
  // honest after a re-render.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, collapsed]);

  // The `autoPlay` attribute covers the first mount; this covers re-expanding
  // the strip, and — the reason it exists — gives us the promise, so a blocked
  // autoplay surfaces the play button instead of a frozen first frame.
  useEffect(() => {
    if (collapsed || reducedMotion) return;
    videoRef.current?.play().catch(() => setPlaying(false));
  }, [collapsed, reducedMotion]);

  if (failed) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="flex w-full shrink-0 items-center gap-2.5 border-b border-border/60 bg-card/40 px-4 py-2 text-left transition-colors hover:bg-card/70"
      >
        <img
          src={NIVA_AVATAR_POSTER}
          alt=""
          className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-primary/40"
        />
        <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-muted-foreground">
          Watch Niva&apos;s intro
        </span>
        <Play className="h-3.5 w-3.5 shrink-0 text-primary" />
      </button>
    );
  }

  return (
    <div className="relative shrink-0 border-b border-border/60 bg-black">
      <video
        ref={videoRef}
        src={INTRO_VIDEO}
        poster={INTRO_POSTER}
        // Reduced motion gets the poster and a play button instead: a talking
        // head that starts itself is the definition of unrequested movement.
        autoPlay={!reducedMotion}
        muted
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          // She has said her piece — hand the panel height back to the chat.
          markIntroSeen();
          setCollapsed(true);
        }}
        onError={() => setFailed(true)}
        // object-COVER, not contain. The clip is a clean 16:9 (960x540) and so is
        // the box — until max-h clamps the height on a short viewport, which
        // leaves the box wider than 16:9 and pillarboxed a contained video with
        // the parent's black either side. Cover fills the box in every case.
        //
        // Anchored to the bottom so the crop comes off the ceiling rather than
        // the burned-in captions along the lower edge, which are the whole
        // reason the muted autoplay is readable.
        className="aspect-video max-h-[38dvh] w-full object-cover object-bottom"
      />

      {/* Tap-to-play, for reduced-motion viewers and for any browser that
          refuses the autoplay anyway. */}
      {!playing && (
        <button
          type="button"
          onClick={() => void videoRef.current?.play().catch(() => setFailed(true))}
          className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors hover:bg-black/15"
          aria-label="Play Niva's introduction"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold-gradient text-primary-foreground shadow-lg">
            <Play className="ml-0.5 h-5 w-5 fill-current" />
          </span>
        </button>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-end gap-1.5 p-2">
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          className="pointer-events-auto rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/75"
          aria-label={muted ? "Unmute Niva's introduction" : "Mute Niva's introduction"}
          title={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => {
            // Dismissing counts as having seen it — nobody wants the thing
            // they just closed to reopen on the next visit to the panel.
            markIntroSeen();
            setCollapsed(true);
          }}
          className="pointer-events-auto rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/75"
          aria-label="Hide Niva's introduction"
          title="Hide intro"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default NivaIntro;
